import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  Button,
  Paper,
  Avatar,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Description as DescriptionIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { ParametroNotificacoes } from './types';
import { notificationConfigService, NotificationSettings } from '../../services/notificationConfigService';
import {
  NotificacoesPush,
  ConfiguracoesGerais,
} from '../notificacoes';
import TemplatesModal from './TemplatesModal';
import { detailPageHeaderPaperSx } from '../../styles/theme';

interface ParametrosNotificacoesProps {
  parametros: ParametroNotificacoes;
  setParametros: React.Dispatch<React.SetStateAction<ParametroNotificacoes>>;
}

const ParametrosNotificacoes: React.FC<ParametrosNotificacoesProps> = ({
  parametros,
  setParametros,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      setLoading(true);
      const settings = await notificationConfigService.getNotificationSettings();

      setParametros(prev => ({
        ...prev,
        notificacoes_push: settings.notificacoes_push,
        horario_inicio_notificacoes: settings.horario_inicio_notificacoes,
        horario_fim_notificacoes: settings.horario_fim_notificacoes,
        nao_enviar_notificacoes_finais_semana: settings.nao_enviar_notificacoes_finais_semana,
        nao_enviar_notificacoes_feriados: settings.nao_enviar_notificacoes_feriados,
        envio_mensagens: settings.envio_mensagens,
        notificacoes_lembretes: settings.notificacoes_lembretes,
      }));
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar configurações de notificação',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveNotificationSetting = async (key: keyof NotificationSettings, value: boolean | string) => {
    try {
      setSaving(true);
      await notificationConfigService.updateNotificationSetting(key, value);

      setSnackbar({
        open: true,
        message: 'Configuração salva com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar configuração',
        severity: 'error'
      });

      setParametros(prev => ({
        ...prev,
        [key]: !value
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = async (field: keyof NotificationSettings, value: boolean | string) => {
    setParametros(prev => ({
      ...prev,
      [field]: value
    }));

    await saveNotificationSetting(field, value);
  };

  const handleChange = (field: keyof ParametroNotificacoes, value: any) => {
    setParametros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box>
      <Paper elevation={0} sx={detailPageHeaderPaperSx(theme)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.15),
                color: 'warning.main',
                width: 44,
                height: 44,
              }}
            >
              <NotificationsIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                Configurações de Notificações
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Configure como e quando o sistema enviará notificações para clientes e usuários
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={() => setTemplatesModalOpen(true)}
            size="small"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 160 },
              flexShrink: 0,
            }}
          >
            Gerenciar Templates
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12}>
          <ConfiguracoesGerais
            parametros={parametros}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <NotificacoesPush
            parametros={parametros}
            saving={saving}
            onNotificationChange={handleNotificationChange}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.info.main, isDark ? 0.35 : 0.25)}`,
              ...(isDark && {
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: 'text.primary',
                '& .MuiAlert-icon': { color: 'info.main' },
              }),
            }}
          >
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Informações importantes
            </Typography>
            <Typography variant="body2" component="div" color="text.secondary">
              • As notificações são enviadas automaticamente com base nos eventos configurados<br />
              • Certifique-se de que os gateways de email e SMS estejam configurados corretamente<br />
              • Os horários de envio ajudam a evitar notificações em horários inadequados<br />
              • Templates personalizados podem ser criados na seção de Templates
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={2}
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.06 : 0.03),
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Carregando configurações...
          </Typography>
        </Box>
      )}

      {saving && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={2}
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.success.main, isDark ? 0.08 : 0.04),
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Salvando...
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <TemplatesModal
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
      />
    </Box>
  );
};

export default ParametrosNotificacoes;
